import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Collapse } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { sanitizeUrlQueryParams } from 'utils/util/util';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import Page from 'views/Layout/Pages/Page';
import { useInfiniteScroll } from 'views/Objectives/ObjectivesOverviewPage/hooks';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import {
  SkillMetadataItem,
  useAgentMetadata,
} from '../AgentsAdmin/useAgentMetadata';
import { useAIAgentsHub } from '../AiAgentsHubContext';
import CreateCustomSkillModal from './CreateCustomSkillModal';
import EditCustomSkillModal from './EditCustomSkillModal';

interface CustomSkill {
  id: number;
  name: string;
  description: string;
  instructions: string;
  required_resources: string[];
  created_at: string;
  updated_at: string;
}

// Expandable built-in skill row
const BuiltInSkillRow: FC<{
  skill: SkillMetadataItem;
  resourceNameMap: Record<string, string>;
  renderInstructions: (text: string) => React.ReactNode;
}> = ({ skill, resourceNameMap, renderInstructions }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="list-group-item">
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ cursor: 'pointer' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div>
          <strong>{skill.name}</strong>
          <p className="text-muted mb-0 small">{skill.description}</p>
        </div>
        <i className={`fe fe-chevron-${isOpen ? 'up' : 'down'} ms-3`} />
      </div>
      <Collapse isOpen={isOpen}>
        <div className="mt-3">
          {skill.required_resources.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-1">
              {skill.required_resources.map((rId) => (
                <Badge key={rId} color="light" className="px-2 py-1">
                  <i className="fe fe-database me-1" />
                  {resourceNameMap[rId] || rId}
                </Badge>
              ))}
            </div>
          )}
          <pre
            className="bg-light p-3 rounded small"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {renderInstructions(skill.instructions)}
          </pre>
        </div>
      </Collapse>
    </div>
  );
};

/**
 * Replace Froala @-mention badge spans with resource badges matching built-in skill styling.
 * Stored HTML: <span class="badge bg-primary-soft ..." data-resource-id="ID" ...>@OldName</span>
 * Output:      <span class="badge bg-light px-2 py-1"><i class="fe fe-database me-1"></i>CurrentName</span>
 */
const resolveResourceMentions = (
  html: string,
  resourceNameMap: Record<string, string>
): string =>
  html.replace(
    /<span[^>]*data-resource-id="([^"]+)"[^>]*>@?[^<]*<\/span>/g,
    (_match, resourceId: string) => {
      const name = resourceNameMap[resourceId] || resourceId;
      return `<span class="badge bg-light px-2 py-1"><i class="fe fe-database me-1"></i>${name}</span>`;
    }
  );

// Expandable custom skill row
const CustomSkillRow: FC<{
  skill: CustomSkill;
  resourceNameMap: Record<string, string>;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ skill, resourceNameMap, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);

  const resolvedInstructions = useMemo(
    () => resolveResourceMentions(skill.instructions, resourceNameMap),
    [skill.instructions, resourceNameMap]
  );

  return (
    <div className="list-group-item">
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ cursor: 'pointer' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex-grow-1">
          <strong>{skill.name}</strong>
          {skill.description && (
            <p className="text-muted mb-0 small">{skill.description}</p>
          )}
        </div>
        <div className="d-flex align-items-center gap-2 ms-3">
          <Button
            size="sm"
            color="white"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(skill.id);
            }}
          >
            <i className="fe fe-edit-2" />
          </Button>
          <Button
            size="sm"
            color="white"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(skill.id);
            }}
          >
            <i className="fe fe-trash-2" />
          </Button>
          <i className={`fe fe-chevron-${isOpen ? 'up' : 'down'}`} />
        </div>
      </div>
      <Collapse isOpen={isOpen}>
        <div className="mt-3">
          {skill.required_resources.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-1">
              {skill.required_resources.map((rId) => (
                <Badge key={rId} color="light" className="px-2 py-1">
                  <i className="fe fe-database me-1" />
                  {resourceNameMap[rId] || rId}
                </Badge>
              ))}
            </div>
          )}
          <div
            className="bg-light p-3 rounded small"
            dangerouslySetInnerHTML={{ __html: resolvedInstructions }}
          />
        </div>
      </Collapse>
    </div>
  );
};

const SkillsAdminPage: FC = () => {
  const { setAction } = useAIAgentsHub();
  const { formatMessage } = useIntl();
  const {
    templates,
    resources,
    isLoading: isMetadataLoading,
  } = useAgentMetadata();

  // Custom skills state
  const [currentUrl, setCurrentUrl] = useState<string>('ai-custom-skills');
  const [nextUrl, setNextUrl] = useState<string | undefined>(undefined);
  const [customSkills, setCustomSkills] = useState<CustomSkill[] | undefined>(
    undefined
  );
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editSkillId, setEditSkillId] = useState<number | undefined>(undefined);
  const [deleteSkillId, setDeleteSkillId] = useState<number | undefined>(
    undefined
  );

  const callback = useCallback(
    (
      data: { results: CustomSkill[]; next?: string; count: number } | null,
      error,
      hardError
    ): void => {
      if (!data || error || hardError) {
        console.error('Error fetching custom skills:', error || hardError);
        return;
      }
      setCustomSkills((prev) => [...(prev ?? []), ...data.results]);
      setNextUrl(
        data.next ? sanitizeUrlQueryParams(data.next, ['page']) : undefined
      );
      setIsLoadingMoreItems(false);
    },
    []
  );

  const { error, status } = useConfirmApi<{
    results: CustomSkill[];
    next?: string;
    count: number;
  }>({
    method: 'GET',
    url: currentUrl,
    callback: callback,
  });

  // Group built-in skills by template
  const templatesWithSkills = useMemo(
    () => templates.filter((t) => t.skills.length > 0),
    [templates]
  );

  // Build a map of internal_tool_name → resource name for instruction interpolation
  const toolNameToResourceName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of resources) {
      if (r.internal_tool_name) {
        map[r.internal_tool_name] = r.name;
      }
    }
    return map;
  }, [resources]);

  // Build a map of resource ID → resource name for badge display
  const resourceNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of resources) {
      map[r.id] = r.name;
    }
    return map;
  }, [resources]);

  // Render instructions text, replacing internal tool names with resource badges
  const renderInstructions = useCallback(
    (text: string): React.ReactNode => {
      const toolNames = Object.keys(toolNameToResourceName);
      if (toolNames.length === 0) {
        return text;
      }
      const sorted = [...toolNames].sort((a, b) => b.length - a.length);
      const pattern = new RegExp(`['"\`]?(${sorted.join('|')})['"\`]?`, 'g');
      const result: React.ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = pattern.exec(text)) !== null) {
        const toolName = match[1];
        const start = match.index;
        if (start > lastIndex) {
          result.push(text.slice(lastIndex, start));
        }
        result.push(
          <Badge key={start} color="light" className="px-2 py-1 mx-1">
            <i className="fe fe-database me-1" />
            {toolNameToResourceName[toolName]}
          </Badge>
        );
        lastIndex = start + match[0].length;
      }
      if (lastIndex < text.length) {
        result.push(text.slice(lastIndex));
      }
      return result;
    },
    [toolNameToResourceName]
  );

  const handleCreateClick = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleCallbackCreateModal = useCallback((createdSkill: unknown) => {
    const skill = createdSkill as CustomSkill;
    setCustomSkills((prev) => (prev ? [skill, ...prev] : [skill]));
  }, []);

  const handleEditClick = useCallback((skillId: number) => {
    setEditSkillId(skillId);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditSkillId(undefined);
  }, []);

  const handleCallbackEditModal = useCallback((updatedSkill: unknown) => {
    const skill = updatedSkill as CustomSkill;
    setCustomSkills((prev) =>
      prev?.map((s) => (s.id === skill.id ? skill : s))
    );
  }, []);

  const handleDeleteClick = useCallback((skillId: number) => {
    setDeleteSkillId(skillId);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteSkillId) return;
    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `ai-custom-skills/${deleteSkillId}`,
      {},
      (data, error) => {
        if (error) {
          toast.error(
            formatMessage({
              id: 'app.views.ai.agents.skills_admin.delete.error',
              defaultMessage: 'Failed to delete skill',
            })
          );
        } else {
          toast.success(
            formatMessage({
              id: 'app.views.ai.agents.skills_admin.delete.success',
              defaultMessage: 'Skill deleted successfully',
            })
          );
          setCustomSkills((prev) =>
            prev?.filter((s) => s.id !== deleteSkillId)
          );
        }
        setDeleteSkillId(undefined);
      }
    );
  }, [deleteSkillId, formatMessage]);

  const loadMoreRef = useRef(null);

  const handleLoadMore = useCallback((nextUrl) => {
    setCurrentUrl(nextUrl);
    setIsLoadingMoreItems(true);
  }, []);

  useInfiniteScroll(
    loadMoreRef,
    useCallback(() => {
      if (nextUrl) {
        handleLoadMore(nextUrl);
      }
    }, [nextUrl, handleLoadMore])
  );

  const createNewSkillButton = useMemo(
    () => (
      <Button color="primary" onClick={handleCreateClick}>
        <FormattedMessage
          id="app.views.ai.agents.skills_admin.create_button"
          defaultMessage="New Custom Skill"
        />
      </Button>
    ),
    [handleCreateClick]
  );

  useEffect(() => {
    setAction(createNewSkillButton);
  }, [createNewSkillButton, setAction]);

  const isLoading = isMetadataLoading || status === 'LOADING';

  if (isLoading) {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.ai.agents.skills_admin.loading',
          defaultMessage: 'Loading skills...',
        })}
      />
    );
  }

  if (error) {
    return <SimpleErrorPage error={error} />;
  }

  return (
    <Page
      embedded={true}
      title={formatMessage({
        id: 'app.views.ai.agents.skills_admin.title',
        defaultMessage: 'Skills',
      })}
    >
      {/* Custom Skills section */}
      <Card className="mb-5">
        <CardBody>
          <h3 className="mb-3">
            <FormattedMessage
              id="app.views.ai.agents.skills_admin.custom.title"
              defaultMessage="Custom Skills"
            />
          </h3>
          <p className="text-muted mb-3">
            <FormattedMessage
              id="app.views.ai.agents.skills_admin.custom.description"
              defaultMessage="Create your own skills with custom instructions. Use @ to reference available resources in the instructions editor."
            />
          </p>

          {!customSkills || customSkills.length === 0 ? (
            <EmptyState
              title={formatMessage({
                id: 'app.views.ai.agents.skills_admin.custom.empty.title',
                defaultMessage: 'No custom skills yet',
              })}
              subtitle={formatMessage({
                id: 'app.views.ai.agents.skills_admin.custom.empty.subtitle',
                defaultMessage:
                  'Create custom skills to give your AI agents specialized instructions for handling specific tasks.',
              })}
            >
              {createNewSkillButton}
            </EmptyState>
          ) : (
            <>
              <div className="list-group">
                {customSkills.map((skill) => (
                  <CustomSkillRow
                    key={skill.id}
                    skill={skill}
                    resourceNameMap={resourceNameMap}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                  />
                ))}
              </div>
              <div ref={loadMoreRef} />
              {isLoadingMoreItems && <Loading />}
            </>
          )}
        </CardBody>
      </Card>

      {/* Built-in Skills section, grouped by template */}
      {templatesWithSkills.length > 0 && (
        <Card className="mb-5">
          <CardBody>
            <h3 className="mb-3">
              <FormattedMessage
                id="app.views.ai.agents.skills_admin.built_in.title"
                defaultMessage="Built-in Skills"
              />
            </h3>
            <p className="text-muted mb-3">
              <FormattedMessage
                id="app.views.ai.agents.skills_admin.built_in.description"
                defaultMessage="These skills are included with your agent templates and cannot be modified."
              />
            </p>
            {templatesWithSkills.map((template) => (
              <div key={template.id} className="mb-4">
                <h5 className="fw-semibold mb-2">{template.name}</h5>
                <div className="list-group">
                  {template.skills.map((skill) => (
                    <BuiltInSkillRow
                      key={skill.id}
                      skill={skill}
                      resourceNameMap={resourceNameMap}
                      renderInstructions={renderInstructions}
                    />
                  ))}
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {/* Modals */}
      {isCreateModalOpen && (
        <CreateCustomSkillModal
          isOpen={isCreateModalOpen}
          toggle={handleCloseCreateModal}
          onSuccess={handleCallbackCreateModal}
          resources={resources}
        />
      )}

      {editSkillId !== undefined && (
        <EditCustomSkillModal
          isOpen={editSkillId !== undefined}
          toggle={handleCloseEditModal}
          skillId={editSkillId}
          onSuccess={handleCallbackEditModal}
          resources={resources}
        />
      )}

      <ConfirmationDialogModal
        isOpen={!!deleteSkillId}
        toggle={() => setDeleteSkillId(undefined)}
        confirmCallback={handleConfirmDelete}
        title={formatMessage({
          id: 'app.views.ai.agents.skills_admin.delete.title',
          defaultMessage: 'Delete Custom Skill?',
        })}
        description={formatMessage({
          id: 'app.views.ai.agents.skills_admin.delete.description',
          defaultMessage:
            'Are you sure you want to delete this custom skill? This action cannot be undone.',
        })}
        confirmText={formatMessage({
          id: 'app.views.ai.agents.skills_admin.delete.confirm',
          defaultMessage: 'Delete',
        })}
      />
    </Page>
  );
};

export default React.memo(SkillsAdminPage);
